import { useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { NavigationModule, NavigationPage } from '../components/layout/types';
import { navigationModules, withCountryAwareTitles, withoutPreviewPages } from '../components/layout/navigationModules';
import { useDemoPreviews } from '../views/Previews/PayrollControls/gating/useDemoPreviews';
import { useNavConfig } from '../api/Navigation/moduleConfig';
import { useComplianceCountry } from './useComplianceCountry';
import { useSignalRConnection } from './useSignalRConnection';
import { useHourlyPayAccess } from '../utils/hourlyPayAccess';
import { useZktecoAccess } from '../utils/zktecoAccess';
import { useTreatAsFree } from './useTreatAsFree';
import { isConnectHiddenModule } from '../data/brochureModules';

interface RootState {
  user: {
    role: string;
    roles?: string[];
    isLoggedIn: boolean;
    isManager: boolean;
    isAssetManager?: boolean;
    userId: string;
  };
}

/**
 * Filters navigation modules based on:
 * 1. User role (local filter)
 * 2. Company module configuration (API-based, Operator controlled)
 * 3. Per-feature runtime conditions carried on the user/company snapshot — compliance country,
 *    shift-swap enablement, and (CO-239) the environment's `Billing:Enabled` master switch.
 *
 * Filtering rules:
 * - If module has no roles array, it's visible to all
 * - If module has roles array, user's role must be in it
 * - Same rules apply to pages within modules
 * - Modules with no visible pages are excluded
 * - Company config further restricts which modules are visible
 * - Empty company config means all modules enabled (backwards compatible), EXCEPT demo surfaces:
 *   those stay hidden until a config read has succeeded and returned rows (CO-851). The rule lives
 *   in the navigation config store (`useNavConfig`), which the route guard reads too, so the
 *   sidebar and the guard always decide from the same rows.
 *
 * @returns Filtered array of NavigationModule based on user role and company config
 */
export interface FilteredNavigationState {
  modules: NavigationModule[];
  /**
   * False until the company's module and page config has been fetched, or the fetch has failed.
   * True on a FAILED read too: `modules` then holds every real page (they fail open) and no demo
   * surface (they fail closed, CO-851), and the store keeps retrying in the background.
   */
  loaded: boolean;
}

export function useFilteredNavigationState(): FilteredNavigationState {
  const role = useSelector((state: RootState) => state.user.role);
  const roles = useSelector((state: RootState) => state.user.roles);
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const isManager = useSelector((state: RootState) => state.user.isManager);
  const isAssetManager = useSelector((state: RootState) => state.user.isAssetManager);
  const userId = useSelector((state: RootState) => state.user.userId);
  const complianceCountry = useComplianceCountry();
  // Effective shift-swap enablement = per-user override (Enabled/Disabled) else the company default.
  // Mirrors ShiftSwapService.ResolveShiftSwapEnabled on the server (which stays the security boundary).
  const shiftSwapConfig = useSelector((state: { user?: { userRules?: { shiftSwapConfig?: string } } }) => state.user?.userRules?.shiftSwapConfig);
  const companyShiftSwapEnabled = useSelector((state: { user?: { company?: { shiftSwapEnabled?: boolean } } }) => state.user?.company?.shiftSwapEnabled);
  // CO-239 — the ENVIRONMENT's billing master switch (`Billing:Enabled`), delivered on the company
  // object by GetUserInfo. NOT a per-company setting despite where it rides.
  const billingEnabled = useSelector((state: { user?: { company?: { billingEnabled?: boolean } } }) => state.user?.company?.billingEnabled);
  const companyId = useSelector((state: { user?: { company?: { id?: number } } }) => state.user?.company?.id);
  // Hourly pay (docs/hourly-pay-spec.md §7.2) — GetUserInfo's payBasis + hourlyPayEnabled. Fails
  // closed: an unknown or absent flag hides both entries, which keeps the feature dark on old servers.
  const hourlyPay = useHourlyPayAccess();
  const hourlyCompanyEnabled = hourlyPay.companyEnabled;
  const showMyShifts = hourlyPay.showMyShifts;
  // ZKTeco (owner 2026-09-29): Device Sync only for a company with a ZKTeco connection (GetUserInfo flag).
  const zktecoConnected = useZktecoAccess().connected;
  // UI-only demo previews (`previewOnly` pages) — the server's DemoPreviews runtime flag. Fails CLOSED while
  // loading. Deliberately NOT page config / DemoSurfaceDefaults: see views/Previews/PayrollControls/gating/useDemoPreviews.ts.
  const demoPreviewsEnabled = useDemoPreviews().enabled;
  // Mowazaf Connect (tier Free, resolved, non-Operator, or the Operator `?previewTier=free` preview).
  // Same predicate MasterLayout uses for its LockedShell decision — see hooks/useTreatAsFree.ts.
  const treatAsFree = useTreatAsFree();
  const { subscribeToCompanyGroup } = useSignalRConnection();
  // CO-851: the company's module and page config comes from the ONE shared store. This hook used to
  // run its own fetch, which could fail (and fail open) independently of the route guard's.
  const navConfig = useNavConfig();
  const companyConfig = navConfig.state.modules;
  // True once a read has come back, good or failed, or nobody is signed in. A failed read still
  // clears the sidebar skeleton: real pages fail open, only demo surfaces stay hidden.
  const configLoaded = navConfig.settled;

  // The shell is what keeps the SignalR connection alive (`useSignalRConnection` above) and joins the
  // company group that `ModuleConfigUpdated` is broadcast to. The store listens for the event itself
  // and refetches for every consumer at once, so nothing subscribes to it here any more. The company
  // group is idempotently shared with useCompanyRulesSignalR.
  useEffect(() => {
    if (!isLoggedIn || !companyId) return;

    subscribeToCompanyGroup(companyId).catch(err => {
      console.error('Failed to subscribe to company group:', err);
    });
  }, [isLoggedIn, companyId, subscribeToCompanyGroup]);

  return useMemo(() => {
    if (!isLoggedIn) return { modules: [], loaded: configLoaded };

    // Build effective roles: the user's role + "Manager" if isManager flag is set
    const effectiveRoles = new Set([role]);
    if (isManager) effectiveRoles.add('Manager');
    if (isAssetManager) effectiveRoles.add('AssetManagement');

    const hasRole = (allowedRoles: string[]) =>
      allowedRoles.some(r => effectiveRoles.has(r));

    // A page/sub-module with NO `roles` array INHERITS its parent module's gate. That is deliberate
    // (most modules want it), but it means a role-less leaf under a permissive module gate is offered
    // to every role in that gate — and if the leaf's App.tsx route is narrower, the menu entry
    // dead-ends in a 403. Leaves under such modules (notably Settings, whose module gate includes
    // Operator) must declare explicit `roles` mirroring their route guard. See navigationModules.ts.
    // `primaryRoleOnly` (opt-in per page) matches only roles the user really holds, like RoleProtectedRoute.
    const realRoles = Array.isArray(roles) && roles.length > 0 ? roles : [role];
    const isPageVisibleByRole = (rolesArr?: string[], primaryRoleOnly?: boolean) =>
      !rolesArr || (primaryRoleOnly ? rolesArr.some(r => realRoles.includes(r)) : hasRole(rolesArr));

    // First: filter by role (modules, their direct pages, sub-modules, and sub-module pages)
    let filtered: NavigationModule[] = navigationModules
      .filter(module => isPageVisibleByRole(module.roles))
      .map(module => {
        // Modules under the "Admin Section" separator are hidden from users whose
        // only elevated role is Admin (deliberate platform-config scoping) — EXCEPT
        // individual pages that explicitly opt in via `adminVisible` (e.g. the Asset
        // Management settings screen where Admin grants the asset-manager tag).
        // Non-Admin users and non-isAdmin modules are unaffected.
        const adminSectionHidden = role === 'Admin' && !!module.isAdmin;
        const pageVisible = (page: NavigationPage) =>
          isPageVisibleByRole(page.roles, page.primaryRoleOnly) && (!adminSectionHidden || page.adminVisible === true);
        return {
          ...module,
          pages: module.pages.filter(pageVisible),
          subModules: module.subModules
            ?.filter(sub => isPageVisibleByRole(sub.roles))
            .map(sub => ({
              ...sub,
              pages: sub.pages.filter(pageVisible),
            }))
            .filter(sub => sub.pages.length > 0),
        };
      })
      // Remove modules with no visible pages AND no visible sub-modules
      .filter(module => module.pages.length > 0 || (module.subModules?.length ?? 0) > 0);

    // Second: filter by company module config.
    // A module (or sub-module) with no row is shown; one with an explicit OFF row is not. That is
    // the rule the store applies, and for a real module it is all of it: an empty or failed read
    // hides nothing. A DEMO module is additionally hidden until a modules read has succeeded and
    // returned rows (CO-851), which is why this runs even when the list is empty: skipping the block
    // on an empty list, as this used to, is exactly how a failed read put demo modules back.
    //
    // CO-503: the same test for SUB-modules, on the sub-module id. Keeping the sidebar and
    // `PageConfigGuard` in step is the point: a visible entry whose page answers "turned off for
    // your company" is the split the page-config feature exists to prevent. Both now ask the store.
    filtered = filtered
      .filter(module => navConfig.isModuleEnabled(module.id))
      .map(module => ({
        ...module,
        subModules: module.subModules?.filter(sub => navConfig.isModuleEnabled(sub.id)),
      }))
      .filter(module => module.pages.length > 0 || (module.subModules?.length ?? 0) > 0);

    if (companyConfig.length > 0) {
      /* ──────────────────────────────────────────────────────────────────────────────────────
         Apply display order from config, falling back to a module's position in
         `navigationModules` so a newly added module slots in at its source-defined location
         instead of being dumped after the Admin section.

         🔴 A NULL STORED ORDER MEANS "UNKNOWN", NOT "LAST", AND THE DIFFERENCE IS THE WHOLE BUG.

         This read `c.displayOrder ?? 999`, which collapsed every NULL-ordered row to 999 BEFORE
         the `?? sourceOrder` fallback could ever be consulted -- so the fallback the comment
         promises was unreachable for exactly the rows that needed it.

         `NavigationController.UpdateCompanyModuleConfig` deletes and re-inserts every row on save,
         and `Settings/ModuleConfig` sends no `displayOrder` at all, so EVERY company whose module
         config has ever been saved from that screen has all-NULL ordering. On such a tenant every
         configured module tied at 999 while a module with NO row -- e.g. `integrations`, added
         2026-08-24 and therefore absent from every pre-existing config -- took the source index 12
         and sorted ABOVE all of them. Integrations is `isAdmin`, and `Sidebar` renders the
         "ADMIN SECTION" divider before the first admin module, so the heading landed at the very
         top of the sidebar and appeared to label Home, Core HR and Employee Impact as admin items.

         Building the map only from rows that actually carry an order makes the two scales
         comparable again: a real stored order wins, and anything else -- NULL row or no row --
         falls back to source order, which is what the comment always said.
         ────────────────────────────────────────────────────────────────────────────────────── */
      const orderMap = new Map(
        companyConfig
          .filter((c) => c.displayOrder !== null && c.displayOrder !== undefined)
          .map((c) => [c.moduleId, c.displayOrder as number])
      );
      const sourceOrder = new Map(
        navigationModules.map((m, i) => [m.id, i])
      );
      filtered.sort((a, b) => {
        const orderA = orderMap.get(a.id) ?? sourceOrder.get(a.id) ?? 999;
        const orderB = orderMap.get(b.id) ?? sourceOrder.get(b.id) ?? 999;
        return orderA - orderB;
      });
    }

    // Third: filter by page config. Same shape as the module filter above: a page with an explicit
    // OFF row is dropped, a page with no row is kept, and a DEMO page is also dropped until a pages
    // read has succeeded and returned rows (CO-851). Runs on an empty list for that last reason.
    // Matched on the registry path, before `:userId` is resolved below, as the rows store it.
    const pageEnabled = (page: NavigationPage) => navConfig.isPageRowEnabled(page.path);
    filtered = filtered.map(module => ({
      ...module,
      pages: module.pages.filter(pageEnabled),
      subModules: module.subModules
        ?.map(sub => ({
          ...sub,
          pages: sub.pages.filter(pageEnabled),
        }))
        .filter(sub => sub.pages.length > 0),
    })).filter(module =>
      module.pages.length > 0 || (module.subModules?.length ?? 0) > 0
    );

    // Resolve dynamic path tokens (e.g., :userId) — applies to direct + sub-module pages
    if (userId) {
      const resolvePath = (page: NavigationPage): NavigationPage =>
        page.path.includes(':userId')
          ? { ...page, path: page.path.replace(':userId', userId) }
          : page;
      filtered = filtered.map(module => ({
        ...module,
        pages: module.pages.map(resolvePath),
        subModules: module.subModules?.map(sub => ({
          ...sub,
          pages: sub.pages.map(resolvePath),
        })),
      }));
    }

    // Filter compliance pages by country:
    // - Compliance Center, WPS Center, and Compliance Settings are shared by
    //   UAE and KSA (both have WPS-like mandates and country-specific identifiers).
    // - EOS Center embeds UAE-specific gratuity rules and stays UAE-only.
    // Per-profile EOS and EOS settings are generic and stay visible everywhere.
    const SHARED_COMPLIANCE_PATHS = new Set([
      '/payroll/compliance-center',
      '/settings/payroll/compliance',
      '/payroll/wps-center',
      '/payroll/pension',
    ]);
    const UAE_ONLY_PATHS = new Set([
      '/eos-center',
    ]);
    const isUaeOrKsa = complianceCountry === 'UAE' || complianceCountry === 'KSA';
    const isOperator = role === 'Operator';
    const isCompliancePageVisible = (page: { path: string }) => {
      if (page.path === '/settings/payroll/compliance' && isOperator) return true;
      // Hide shared compliance pages from tenants outside UAE/KSA
      if (!isUaeOrKsa && SHARED_COMPLIANCE_PATHS.has(page.path)) return false;
      // Hide UAE-only pages from any non-UAE tenant (including KSA)
      if (complianceCountry !== 'UAE' && UAE_ONLY_PATHS.has(page.path)) return false;
      return true;
    };
    // Apply to direct pages AND sub-module pages, and keep any module that
    // still has content in either bucket. (The Settings module is a tree of
    // sub-modules with empty top-level `pages`, so checking only `pages.length`
    // here would drop it entirely for HR/Admin.)
    filtered = filtered.map(module => ({
      ...module,
      pages: module.pages.filter(isCompliancePageVisible),
      subModules: module.subModules
        ?.map(sub => ({
          ...sub,
          pages: sub.pages.filter(isCompliancePageVisible),
        }))
        .filter(sub => sub.pages.length > 0),
    })).filter(module =>
      module.pages.length > 0 || (module.subModules?.length ?? 0) > 0,
    );

    // Hide the Shift Swaps page unless the feature is enabled for this user. The parent "attendance"
    // module keeps its other pages, so this only drops the single item (and prunes an empty module).
    const shiftSwapEnabled =
      shiftSwapConfig === 'Enabled' ? true :
      shiftSwapConfig === 'Disabled' ? false :
      !!companyShiftSwapEnabled;
    if (!shiftSwapEnabled) {
      const notShiftSwap = (page: { path: string }) => page.path !== '/shift-swaps';
      filtered = filtered.map(module => ({
        ...module,
        pages: module.pages.filter(notShiftSwap),
        subModules: module.subModules
          ?.map(sub => ({ ...sub, pages: sub.pages.filter(notShiftSwap) }))
          .filter(sub => sub.pages.length > 0),
      })).filter(module =>
        module.pages.length > 0 || (module.subModules?.length ?? 0) > 0,
      );
    }

    // CO-239 — hide the Billing entry unless the environment's billing subsystem is actually on.
    // The role gate on the module (HR/Admin) is necessary but NOT sufficient: with Billing:Enabled
    // false the page still renders, against a subscription row no billing job has ever touched. A
    // backfilled perpetual tenant (NeverExpires = 1, CommittedSeats = 0) is then told it is "N seats
    // over" — the entire headcount reads as overage — quoted "0 seats at AED 0.00", and shown a term
    // ending 9999-12-31; a Free tenant's plan chooser dead-ends on a raw 409 BILLING_DISABLED.
    //
    // 🔴 FAIL CLOSED on undefined. A redux-persist snapshot written before this field existed carries
    // no `billingEnabled`, and a stale "billing is on" is exactly the state this is meant to prevent
    // (it is the returning session of a legacy tenant on deploy day). The cost of the other direction
    // is a briefly missing menu entry on an environment where billing IS on, which `revalidateUserConfig`
    // repairs on the next reconcile without a re-login.
    //
    // The pruning shape mirrors the shift-swap filter above: drop the page, then drop any sub-module
    // and module left empty, so the Settings tree does not keep an empty "Billing & Subscription" node.
    // Keep this in step with the /settings/billing route guard in App.tsx.
    if (billingEnabled !== true) {
      const notBilling = (page: { path: string }) => !page.path.startsWith('/settings/billing');
      filtered = filtered.map(module => ({
        ...module,
        pages: module.pages.filter(notBilling),
        subModules: module.subModules
          ?.map(sub => ({ ...sub, pages: sub.pages.filter(notBilling) }))
          .filter(sub => sub.pages.length > 0),
      })).filter(module =>
        module.pages.length > 0 || (module.subModules?.length ?? 0) > 0,
      );
    }

    // Hourly pay — the approval queue needs the company switch on; My shifts additionally needs the
    // signed-in user to be Hourly. Same pruning shape as the two filters above. Keep in step with
    // HourlyPayRoute in App.tsx.
    const featureHidden = new Set<string>();
    if (!hourlyCompanyEnabled) featureHidden.add('/attendance/shift-approvals');
    if (!showMyShifts) featureHidden.add('/attendance/my-shifts');
    // Same pruning for Device Sync. Keep in step with DeviceSyncRoute in App.tsx.
    if (!zktecoConnected) featureHidden.add('/attendance/device-sync');
    if (featureHidden.size > 0) {
      const notHidden = (page: { path: string }) => !featureHidden.has(page.path);
      filtered = filtered.map(module => ({
        ...module,
        pages: module.pages.filter(notHidden),
        subModules: module.subModules
          ?.map(sub => ({ ...sub, pages: sub.pages.filter(notHidden) }))
          .filter(sub => sub.pages.length > 0),
      })).filter(module =>
        module.pages.length > 0 || (module.subModules?.length ?? 0) > 0,
      );
    }

    if (!demoPreviewsEnabled) filtered = withoutPreviewPages(filtered);

    // Owner request 2026-10-05: "On Mowazaf Connect, please remove showcasing the recruitment module
    // and the tournaments module." On a Connect tenant these entries only ever opened the LockedShell
    // upsell, so they are dropped from the navigation itself (CONNECT_HIDDEN_MODULE_SLUGS,
    // data/brochureModules.ts). Running it HERE rather than in the Sidebar means the outlet context,
    // PageConfigGuard's escape path and HomeRoute's landing pick all see the same list. Fails OPEN
    // (unresolved or errored /me → full list) and writes no config, so Paid/Trial/HRMS tenants,
    // Operators and an upgraded tenant are unchanged.
    if (treatAsFree) filtered = filtered.filter(module => !isConnectHiddenModule(module.id));

    // Country-specific labels: a UAE company reads "Pension (GPSSA)", a KSA company "GOSI Contributions".
    // While the compliance country is unknown ('None'), every page keeps its static title.
    filtered = withCountryAwareTitles(filtered, complianceCountry);

    return { modules: filtered, loaded: configLoaded };
  }, [role, roles, isLoggedIn, isManager, isAssetManager, userId, navConfig, companyConfig, configLoaded, complianceCountry, shiftSwapConfig, companyShiftSwapEnabled, billingEnabled, hourlyCompanyEnabled, showMyShifts, zktecoConnected, demoPreviewsEnabled, treatAsFree]);
}

/** The modules alone, for callers that only render navigation. */
export function useFilteredNavigation(): NavigationModule[] {
  return useFilteredNavigationState().modules;
}

export default useFilteredNavigation;
